import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, downloadFile, blobErrorDetail, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Download, FileUp } from "lucide-react";

const STATUS = {
  new: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-blue-50 text-blue-700 border-blue-200",
  unchanged: "bg-slate-50 text-slate-500 border-slate-200",
  duplicate_in_file: "bg-amber-50 text-amber-800 border-amber-200",
  error: "bg-red-50 text-red-700 border-red-200",
};

export const ImportDialog = ({ entity, label, onImported, paths }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);

  const url = {
    template: paths?.template || `/admin/master-data/${entity}/template`,
    preview: paths?.preview || `/admin/master-data/${entity}/preview`,
    commit: paths?.commit || `/admin/master-data/${entity}/commit`,
  };

  const getTemplate = async () => {
    setDl(true);
    try {
      await downloadFile(url.template, `airava_${entity}_template.xlsx`);
    } catch (e) {
      toast.error(await blobErrorDetail(e));
    } finally {
      setDl(false);
    }
  };

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(url.preview, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setResult(null); setOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(url.commit, { rows: preview.rows });
      setResult(data);
      toast.success(`${data.created} created · ${data.updated} updated`);
      onImported();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const applicable = preview ? preview.summary.new + preview.summary.update : 0;
  const fmt = (v) => (Array.isArray(v) ? v.join(", ") : v ?? "");

  return (
    <>
      <Button variant="outline" onClick={getTemplate} disabled={dl} data-testid={`import-template-btn-${entity}`}><Download className="h-4 w-4 mr-2" />{dl ? "Preparing…" : "Excel template"}</Button>
      <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid={`import-file-input-${entity}`} />
      <Button onClick={() => ref.current?.click()} disabled={busy} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid={`import-upload-btn-${entity}`}><FileUp className="h-4 w-4 mr-2" />{busy ? "Reading…" : `Import ${label}`}</Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Import preview — {label}</DialogTitle>
            <DialogDescription>Rows are validated and checked against existing records. Only <strong>new</strong> and <strong>update</strong> rows are applied; duplicates and errors are skipped.</DialogDescription>
          </DialogHeader>
          {preview && (
            <>
              <div className="flex flex-wrap gap-2" data-testid="import-summary">
                {Object.entries(preview.summary).map(([k, v]) => <Badge key={k} variant="outline" className={`${STATUS[k]} capitalize`}>{k.replace(/_/g, " ")}: {v}</Badge>)}
              </div>
              <div className="max-h-[50vh] overflow-auto rounded-md border border-slate-200">
                <Table>
                  <TableHeader className="bg-slate-50"><TableRow><TableHead className="w-12">Row</TableHead><TableHead>Status</TableHead>{preview.columns.map((c) => <TableHead key={c} className="capitalize">{c.replace(/_/g, " ")}</TableHead>)}</TableRow></TableHeader>
                  <TableBody>
                    {preview.rows.map((r) => (
                      <TableRow key={r.row} data-testid={`import-row-${r.row}`}>
                        <TableCell className="font-mono text-xs">{r.row}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`${STATUS[r.status]} text-[10px] capitalize`}>{r.status.replace(/_/g, " ")}</Badge>
                          {r.errors.length > 0 && <div className="text-[11px] text-red-600 mt-1">{r.errors.join("; ")}</div>}
                        </TableCell>
                        {preview.columns.map((c) => (
                          <TableCell key={c} className="text-xs">
                            {r.changes[c] ? <span><s className="text-slate-400">{fmt(r.changes[c].from) || "—"}</s> → <strong>{fmt(r.changes[c].to)}</strong></span> : fmt(r.data[c])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {result?.temp_passwords?.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs" data-testid="import-temp-passwords">
                  <p className="font-semibold text-amber-900">Temporary passwords (shown once — share securely):</p>
                  <ul className="mt-1 font-mono">{result.temp_passwords.map((t) => <li key={t.email}>{t.email} → {t.temp_password}</li>)}</ul>
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{result ? "Close" : "Cancel"}</Button>
            {!result && <Button onClick={commit} disabled={busy || applicable === 0} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="import-commit-btn"><Upload className="h-4 w-4 mr-2" />Apply {applicable} row(s)</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
